import React, { useRef, useState } from "react";
import { Camera, Video, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

async function uploadVideo(file: File, onProgress: (n: number) => void) {
  const { upload_id, chunk_size } = (await api.post("/review-media/video/init", { filename: file.name, size: file.size, content_type: file.type })).data;
  const n = Math.ceil(file.size / chunk_size);
  for (let i = 0; i < n; i++) {
    await api.put(`/review-media/video/${upload_id}/${i}`, file.slice(i * chunk_size, (i + 1) * chunk_size), { headers: { "Content-Type": "application/octet-stream" } });
    onProgress(Math.round(((i + 1) / n) * 100));
  }
  return (await api.post(`/review-media/video/${upload_id}/complete`)).data;
}

export function ReviewUploads({ photos, setPhotos, video, setVideo }: { photos: string[]; setPhotos: (v: string[]) => void; video: string | null; setVideo: (v: string | null) => void }) {
  const pRef = useRef<HTMLInputElement>(null);
  const vRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const addPhoto = async (f?: File) => {
    if (!f) return;
    setBusy("photo");
    try { const fd = new FormData(); fd.append("file", f); setPhotos([...photos, (await api.post("/review-media/photo", fd)).data.url]); } catch (e) { toast.error(errMsg(e)); }
    setBusy(null); if (pRef.current) pRef.current.value = "";
  };
  const addVideo = async (f?: File) => {
    if (!f) return;
    if (f.size > 50 * 1024 * 1024) { toast.error("Videos must be under 50 MB"); return; }
    setBusy("0");
    try { setVideo((await uploadVideo(f, (n) => setBusy(String(n)))).url); } catch (e) { toast.error(errMsg(e)); }
    setBusy(null); if (vRef.current) vRef.current.value = "";
  };
  const chip = "flex items-center gap-2 border border-neutral-300 px-3 py-2 text-xs hover:border-neutral-900 transition-colors disabled:opacity-50";
  return (
    <div className="space-y-3" data-testid="review-uploads">
      <div className="flex flex-wrap gap-2">
        {photos.map((u) => (
          <div key={u} className="relative h-16 w-16" data-testid="review-photo-thumb"><img src={u} alt="" className="h-full w-full object-cover" />
            <button type="button" onClick={() => setPhotos(photos.filter((x) => x !== u))} className="absolute -top-2 -right-2 bg-white border rounded-full p-0.5" aria-label="Remove photo"><X size={12} /></button></div>
        ))}
        {video && <div className="relative h-16 w-24 bg-black" data-testid="review-video-thumb"><video src={video} className="h-full w-full object-cover" muted />
          <button type="button" onClick={() => setVideo(null)} className="absolute -top-2 -right-2 bg-white border rounded-full p-0.5" aria-label="Remove video"><X size={12} /></button></div>}
      </div>
      <div className="flex flex-wrap gap-2">
        {photos.length < 3 && <button type="button" disabled={!!busy} onClick={() => pRef.current?.click()} className={chip} data-testid="review-add-photo-button">
          {busy === "photo" ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}Add photo</button>}
        {!video && <button type="button" disabled={!!busy} onClick={() => vRef.current?.click()} className={chip} data-testid="review-add-video-button">
          {busy && busy !== "photo" ? <><Loader2 size={14} className="animate-spin" />Uploading {busy}%</> : <><Video size={14} />Add video</>}</button>}
      </div>
      <input ref={pRef} type="file" accept="image/*" className="hidden" onChange={(e) => addPhoto(e.target.files?.[0])} data-testid="review-photo-input" />
      <input ref={vRef} type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden" onChange={(e) => addVideo(e.target.files?.[0])} data-testid="review-video-input" />
    </div>
  );
}

export function ReviewMediaView({ r }: { r: any }) {
  if (!r.photos?.length && !r.video) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-4" data-testid="review-media">
      {(r.photos || []).map((u: string) => <a key={u} href={u} target="_blank" rel="noreferrer"><img src={u} alt="Customer photo" loading="lazy" className="h-20 w-20 object-cover" /></a>)}
      {r.video && <video src={r.video} controls preload="metadata" className="h-40 max-w-full bg-black" data-testid="review-video" />}
    </div>
  );
}
